/* eslint-disable @typescript-eslint/no-explicit-any -- SDK generics are deliberately broad */
import { useEffect, useRef } from 'react';
import type { DbConnectionBuilder, DbConnectionImpl } from 'spacetimedb';
import { ConnectionManager } from 'spacetimedb';
import { useSpacetimeDB, useTable } from 'spacetimedb/react';
import { tables as launcherTables } from '@sdk/launcher';
import { useAuthKit } from '../contexts/AuthProvider';
import {
  probeLauncherStatus,
  setActiveLauncherUser,
} from '../services/launcher/launchService';
import {
  resetLauncherProbe,
  setLauncherProbe,
} from '../services/launcher/launcherProbeStore';

/** Poll cadence while the STDB state says "launcher not running". */
const PROBE_INTERVAL_MS = 15_000;
/** Backoff ceiling once the local API has proven unreachable. Chrome logs a
 *  native `net::ERR_CONNECTION_REFUSED` console line for EVERY refused fetch
 *  (uncatchable from JS), so a fixed 15 s cadence floods the console for
 *  anyone without the launcher EXE installed. Detection doesn't suffer from
 *  backing off: a launcher that starts up connects to STDB and lands an App
 *  row in `my_connected_clients`, which flips `shouldProbe` off without any
 *  probe involvement — the probe is only the stale-subscription tie-breaker. */
const MAX_PROBE_INTERVAL_MS = 300_000;
/** Min gap between stale-subscription rebuilds triggered by the probe. */
const REBUILD_COOLDOWN_MS = 60_000;
/** Max rebuilds per yellow episode. If the App row still doesn't show up
 *  after this many resubscriptions, rebuilding isn't the cure (e.g. the
 *  launcher's STDB identity differs from this tab's); the probe result
 *  alone keeps the UI truthful. */
const MAX_REBUILDS_PER_EPISODE = 3;

/**
 * Local-launcher probe driver. Mount exactly once (lives in
 * `OuterReconnectHarness` next to the reconnect hooks).
 *
 * Whenever the launcher connection is active but `my_connected_clients` has
 * no App row (the yellow rocket), this polls the launcher's local HTTP API
 * `GET /status` and publishes the result to `launcherProbeStore` so the
 * rocket UI can distinguish:
 *   - unreachable: launcher genuinely not running (yellow is correct)
 *   - reachable, same user: the STDB subscription went stale (a half-open
 *     websocket freezes the table cache without any close event, which the
 *     silent-disconnect watchdog can't see). We force a ConnectionManager
 *     rebuild to resubscribe, and the UI shows green meanwhile since the
 *     LocalAPI launch path works regardless.
 *   - reachable, different user: two browsers / two accounts on one machine.
 *     The single launcher instance is signed in as someone else; the UI
 *     offers a "switch launcher user" action instead of Start/Download.
 */
export function useLauncherProbeDriver<T extends DbConnectionImpl<any>>(
  poolKey: string,
  builderFactory: () => DbConnectionBuilder<T>,
): void {
  const launcherState = useSpacetimeDB('launcher');
  const [connectedClients] = useTable(
    'launcher',
    launcherTables.my_connected_clients,
  );
  const { user } = useAuthKit();
  const myEmail = user?.email ?? null;
  const mySub = user?.id ?? null;

  // Route local-API discovery to THIS user's launcher — parallel launcher
  // instances (one per profile) bind consecutive ports, and the probe must
  // find ours, not whoever grabbed 19871 first.
  useEffect(() => {
    setActiveLauncherUser(mySub);
  }, [mySub]);

  const stdbSaysRunning = connectedClients.some(
    (c) => c.clientType?.tag === 'App',
  );
  const shouldProbe = launcherState.isActive && !stdbSaysRunning;

  const factoryRef = useRef(builderFactory);
  factoryRef.current = builderFactory;
  const lastRebuildAtRef = useRef(0);
  const rebuildsThisEpisodeRef = useRef(0);
  const consecutiveMissesRef = useRef(0);

  useEffect(() => {
    if (!shouldProbe) {
      rebuildsThisEpisodeRef.current = 0;
      consecutiveMissesRef.current = 0;
      resetLauncherProbe();
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Self-scheduling timeout chain (not setInterval) so the gap can grow:
    // 15 s → 30 s → 60 s → … → 5 min while the local API keeps refusing.
    const schedule = () => {
      if (cancelled) return;
      const delay = Math.min(
        PROBE_INTERVAL_MS * 2 ** Math.min(consecutiveMissesRef.current, 10),
        MAX_PROBE_INTERVAL_MS,
      );
      timer = setTimeout(() => void tick(), delay);
    };

    const tick = async () => {
      // A hidden tab's timers are throttled anyway; skip the fetch so a
      // backgrounded portal doesn't keep poking the launcher.
      if (typeof document !== 'undefined' && document.hidden) {
        schedule();
        return;
      }

      const result = await probeLauncherStatus();
      if (cancelled) return;

      if (!result.reachable) {
        consecutiveMissesRef.current += 1;
        setLauncherProbe({
          status: 'unreachable',
          email: null,
          sub: null,
          version: null,
          probedAt: Date.now(),
        });
        schedule();
        return;
      }

      consecutiveMissesRef.current = 0;
      setLauncherProbe({
        status: 'running',
        email: result.userEmail,
        sub: result.userSub,
        version: result.version,
        probedAt: Date.now(),
      });

      // Reachable local API + same signed-in user + empty
      // my_connected_clients cache is proof the subscription is stale:
      // rebuild the pooled connection so the cache resyncs. Match on `sub`
      // first (always present); email is per-environment JWT-template config.
      const sameUser =
        (result.userSub && mySub && result.userSub === mySub) ||
        (result.userEmail &&
          myEmail &&
          result.userEmail.toLowerCase() === myEmail.toLowerCase());
      if (
        sameUser &&
        Date.now() - lastRebuildAtRef.current >= REBUILD_COOLDOWN_MS &&
        rebuildsThisEpisodeRef.current < MAX_REBUILDS_PER_EPISODE
      ) {
        lastRebuildAtRef.current = Date.now();
        rebuildsThisEpisodeRef.current += 1;
        console.info(
          '[stdb:launcher] local API reachable but no App row in cache; rebuilding stale subscription',
        );
        ConnectionManager.rebuild(poolKey, factoryRef.current());
      }
      schedule();
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [shouldProbe, myEmail, mySub, poolKey]);
}
